import type { CoachCategoriesApiGateway } from "@modules/coachCategories/gateway/coachCategoriesApiGateway"
import {
  CategoryProgramVisibility,
  type CategoryProgramBodyInputDto,
  type CategoryProgramDto,
  type CategorySlotDefaultInputDto,
  type CategorySlotDefaultValueDto,
} from "@modules/coachCategories/gateway/coachCategoriesDto"

const makeCategory = (id: string, name: string): CategoryProgramDto => ({
  id,
  name,
  description: "",
  color: "#6C5CE7",
  visibility: CategoryProgramVisibility.COACH_MEMBERS,
  defaultHideSessionContent: false,
  archived: false,
  subscriberSlotVisibility: false,
  image: null,
  restrictions: [],
})

const makeSlotDefaults = (programId: string): CategorySlotDefaultValueDto => ({
  id: `slot-defaults-${programId}`,
  programId,
  duration: 60,
  participantLimit: null,
  location: null,
  participation: null,
  trialsAccepted: true,
})

const FAKE_CATEGORIES: CategoryProgramDto[] = [
  makeCategory("cat-1", "Bilan"),
  makeCategory("cat-2", "Entretien téléphonique"),
]

export const COACH_CATEGORIES_TEST_DATA = {
  FAKE_CATEGORIES,
}

export class FakeCoachCategoriesApi implements CoachCategoriesApiGateway {
  categories: CategoryProgramDto[] = [...FAKE_CATEGORIES]
  slotDefaults: Record<string, CategorySlotDefaultValueDto> = Object.fromEntries(
    FAKE_CATEGORIES.map((category) => [category.id, makeSlotDefaults(category.id)]),
  )

  givenCategories(categories: CategoryProgramDto[]) {
    this.categories = categories
  }

  getCoachCategories(): Promise<CategoryProgramDto[]> {
    return Promise.resolve(this.categories)
  }

  getCoachCategory(id: string): Promise<CategoryProgramDto> {
    const category = this.categories.find((c) => c.id === id)
    if (!category) {
      return Promise.reject(new Error(`Category ${id} not found`))
    }
    return Promise.resolve(category)
  }

  createCoachCategory(input: CategoryProgramBodyInputDto): Promise<CategoryProgramDto> {
    const id = `cat-${Date.now().toString()}`
    const created: CategoryProgramDto = {
      id,
      name: input.name,
      description: input.description,
      color: input.color,
      visibility: input.visibility,
      defaultHideSessionContent: input.defaultHideSessionContent,
      archived: false,
      subscriberSlotVisibility: input.subscriberSlotVisibility,
      image: null,
      restrictions: input.restrictions ?? [],
    }
    this.categories = [...this.categories, created]
    this.slotDefaults[id] = makeSlotDefaults(id)
    return Promise.resolve(created)
  }

  updateCoachCategory(id: string, input: CategoryProgramBodyInputDto): Promise<CategoryProgramDto> {
    const existing = this.categories.find((c) => c.id === id)
    const updated: CategoryProgramDto = {
      id,
      name: input.name,
      description: input.description,
      color: input.color,
      visibility: input.visibility,
      defaultHideSessionContent: input.defaultHideSessionContent,
      archived: existing?.archived ?? false,
      subscriberSlotVisibility: input.subscriberSlotVisibility,
      image: existing?.image ?? null,
      restrictions: input.restrictions ?? [],
    }
    this.categories = this.categories.map((c) => (c.id === id ? updated : c))
    return Promise.resolve(updated)
  }

  getCoachCategorySlotDefaults(id: string): Promise<CategorySlotDefaultValueDto> {
    return Promise.resolve(this.slotDefaults[id] ?? makeSlotDefaults(id))
  }

  updateCoachCategorySlotDefaults(
    id: string,
    input: CategorySlotDefaultInputDto,
  ): Promise<CategorySlotDefaultValueDto> {
    const updated: CategorySlotDefaultValueDto = { id: `slot-defaults-${id}`, programId: id, ...input }
    this.slotDefaults[id] = updated
    return Promise.resolve(updated)
  }

  deleteCoachCategory(id: string): Promise<void> {
    this.categories = this.categories.filter((c) => c.id !== id)
    this.slotDefaults = Object.fromEntries(Object.entries(this.slotDefaults).filter(([key]) => key !== id))
    return Promise.resolve()
  }
}
